import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./file_upload.scss";
import { SecondaryBody } from "@sprout/typography";
import { TextInput } from "@sprout/text_input";
import FileIcon from "@sprout/icons/file";
import CloseIcon from "@sprout/icons/close";
import { IconButton } from "@sprout/icon_button";
import { SecondaryButton } from "@sprout/buttons";
import type UploadManager from "../../utils/uploads/upload_manager";
import classNames from "classnames";
import { type UploadedFile } from "@utils/uploads/uploaded_file.ts";
import { useDropboxChooser } from "@hooks/use_dropbox_chooser.ts";
import { useBoardConfiguration } from "@hooks/use_board_configuration.ts";
import GoogleDrivePicker from "../../utils/uploads/google_drive_picker";

export interface FileUploadData {
  file?: UploadedFile;
  text?: string;
}

interface FileUploadProps {
  id: string;
  manualInputId?: string;
  label: string;
  uploadManager: UploadManager;
  filetypes?: string[];
  required?: boolean;
  allowManual?: boolean;
  loadDropbox?: boolean;
  loadGoogleDrive?: boolean | null;
  errorMessage?: string;
  onChange: (value: FileUploadData) => void;
  fileName?: string;
}

const FileUpload = (props: FileUploadProps) => {
  const [showManualUploader, setShowManualUploader] = useState(false);
  const [fileName, setFileName] = useState(props.fileName || "");
  const [uploading, setUploading] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const [manualInput, setManualInput] = useState("");
  const [uploadError, setUploadError] = useState("");

  const { preview_mode } = useBoardConfiguration();
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<GoogleDrivePicker | null>(null);

  const {
    id,
    manualInputId,
    label,
    uploadManager,
    filetypes = ["pdf", "doc", "docx", "txt", "rtf"],
    required = false,
    allowManual = false,
    loadDropbox = false,
    loadGoogleDrive = false,
    errorMessage = "",
    onChange,
  } = props;

  const { t } = useTranslation("job_post");

  useEffect(() => {
    if (percentage >= 100 && fileName) {
      setUploading(false);
    }
  }, [fileName, percentage]);

  useEffect(() => {
    setFileName(props.fileName || "");
  }, [props.fileName]);

  const setUpload = useCallback(
    (name: string, url: string, expiresAt: number) => {
      setFileName(name);
      setShowManualUploader(false);
      setManualInput("");
      setUploadError("");
      onChange({ file: { name, url, expiresAt } });
    },
    [onChange],
  );

  const uploadToS3 = useCallback(
    async (file: File) => {
      const uploader = uploadManager.uploaders[id];
      await uploader.uploadFile(file, setPercentage);
      setUpload(file.name, uploader.fileUrl(), uploader.expiresAt());
    },
    [id, uploadManager, setUpload],
  );

  const { openDropboxChooser, dropbox } = useDropboxChooser(loadDropbox);

  if (!pickerRef.current) {
    pickerRef.current = new GoogleDrivePicker({ uploadToS3, setUploadError, setUploading });
  }

  const allowedFileExtensions = useMemo(() => filetypes.map((type) => `.${type}`), [filetypes]);

  const openFileDialog = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    if (preview_mode) return;
    inputRef.current?.click();
  };

  const megabytesToBytes = (mb: number) => mb * 1024 * 1024;
  const MAX_FILE_SIZE = 100;

  const attachFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setUploadError("");
    setUploading(true);

    if (file.size > megabytesToBytes(MAX_FILE_SIZE)) {
      setUploading(false);
      setUploadError(t("file_upload.invalid_file_size", { file_size: MAX_FILE_SIZE }) || "");
      return;
    }

    const uploader = uploadManager.uploaders[id];
    try {
      await uploader.uploadFile(file, setPercentage);
    } catch (err) {
      setUploading(false);
      setPercentage(0);
      onChange({});
      if (err instanceof Error) setUploadError(err.message);
      return;
    }

    setUpload(file.name, uploader.fileUrl(), uploader.expiresAt());
  };

  const removeFile = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setFileName("");
    setPercentage(0);
    onChange({});
  };

  const handleManualUploadToggleClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    if (preview_mode) return;
    setShowManualUploader(!showManualUploader);
  };

  const handleManualInputChange = (value: string) => {
    setManualInput(value);
    onChange({ text: value });
  };

  const handleDropboxUploadClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    if (preview_mode) return;
    setUploadError("");

    if (!dropbox.loaded()) {
      setUploadError(t("file_upload.try_again", { uploader: "Dropbox" }) as string);
      return;
    }
    if (!dropbox.browserSupported()) {
      setUploadError(t("file_upload.unsupported_browser", { uploader: "Dropbox" }) as string);
      return;
    }

    openDropboxChooser(allowedFileExtensions || ["documents", "text"], async (files) => {
      if (files.length === 0) return;
      const dropboxResponse = await fetch(files[0].link);
      const blob = await dropboxResponse.blob();
      setUploading(true);
      const file = new File([blob], files[0].name, { type: blob.type });
      await uploadToS3(file);
    });
  };

  const attachControl = (
    <div>
      <SecondaryButton
        type="button"
        ariaLabel={t("file_upload.aria.attach", { label: label.toLowerCase() }) || ""}
        onClick={openFileDialog}
      >
        {t("file_upload.attach")}
      </SecondaryButton>
      <label className="visually-hidden" htmlFor={id}>
        {t("file_upload.attach")}
      </label>
      <input
        id={id}
        className="visually-hidden"
        type="file"
        accept={allowedFileExtensions.join(",")}
        ref={inputRef}
        onChange={attachFile}
      />
    </div>
  );

  const dropboxControl = (
    <SecondaryButton
      type="button"
      onClick={handleDropboxUploadClick}
      ariaLabel={
        t("file_upload.aria.label", { label: label.toLowerCase(), uploader: "Dropbox" }) || ""
      }
    >
      {t("file_upload.dropbox")}
    </SecondaryButton>
  );

  const googleDriveControl = (
    <SecondaryButton
      type="button"
      onClick={() => (preview_mode ? null : pickerRef.current?.onClick())}
      ariaLabel={
        t("file_upload.aria.label", { label: label.toLowerCase(), uploader: "Google Drive" }) || ""
      }
    >
      {t("file_upload.google")}
    </SecondaryButton>
  );

  const manualControl = (
    <div>
      <SecondaryButton
        type="button"
        ariaLabel={t("file_upload.aria.manual", { label: label.toLowerCase() }) || ""}
        onClick={handleManualUploadToggleClick}
      >
        {t("file_upload.manually")}
      </SecondaryButton>
      <label className="visually-hidden" htmlFor={manualInputId}>
        {t("file_upload.manually")}
      </label>
    </div>
  );

  const renderControls = () => {
    const controls = [attachControl];
    if (loadDropbox) controls.push(dropboxControl);
    if (loadGoogleDrive) controls.push(googleDriveControl);
    if (allowManual) controls.push(manualControl);
    return controls;
  };

  const controls = renderControls();
  const hasError = errorMessage || uploadError;

  const labelClasses = classNames({
    label: true,
    "upload-label": true,
    "upload-label--error": !!hasError,
  });

  const labelId = `upload-label-${id}`;

  return (
    <div role="group" aria-labelledby={labelId} aria-required={required} className="file-upload">
      <div id={labelId} className={labelClasses}>
        {label}
        {required && <span className="required">*</span>}
      </div>
      <div className="file-upload__wrapper">
        {!uploading && !fileName && (
          <div className={"button-container"}>
            {controls.map((control, i) => (
              <div key={i} className="secondary-button">
                {control}
              </div>
            ))}
          </div>
        )}
        {!uploading && !fileName && (
          <p id="accepted-filetypes" className="file-upload__filetypes">
            {`${t("file_upload.filetypes", { file_types: filetypes.join(", ") })}. ${t("file_upload.max_file_size", { file_size: MAX_FILE_SIZE })}`}
          </p>
        )}
        {!uploading && fileName && (
          <div className="file-upload__filename">
            <FileIcon color="#3574d6" />
            <SecondaryBody>{fileName}</SecondaryBody>
            <IconButton label="Remove file" icon={CloseIcon} size="sm" onClick={removeFile} />
          </div>
        )}
        {uploading && (
          <div
            role="progressbar"
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            className="file-upload__progressbar"
          >
            <span style={{ width: percentage + "%" }}></span>
          </div>
        )}
      </div>

      {allowManual && showManualUploader && (
        <TextInput
          id={manualInputId}
          multiline
          required={required}
          rows={6}
          onChange={(event) => handleManualInputChange(event.currentTarget.value)}
          value={manualInput}
        />
      )}

      {hasError && (
        <p id={`${id}-error`} className="helper-text helper-text--error" aria-live="polite">
          {errorMessage} {uploadError}
        </p>
      )}
    </div>
  );
};

export default FileUpload;
